import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import history from "../assets/history.svg";
import * as authAction from "../Store/_redux/AuthStore/authActions";
import * as actions from "../Store/_redux/LeaveStore/leaveAction";
import TransactionsModal from "./modal/LeaveTransactionsModal.jsx";
import api from "../Helper/api";
import { useTranslation } from "react-i18next";
import { formatDate } from "../i18n";
import { LeaveTypeEnum } from "../modal/leave-type";

function ProfileCard() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth.loginUser);
  const transactions = useSelector((state) => state.leave.transactions || []);
  const leaves = useSelector((state) => state.leave.leaves || []);
  const [isModalOpen, setModalOpen] = useState(false);

  const handleTransactions = async () => {
    await dispatch(actions.getLeaveTransactions(auth.tc));
    setModalOpen(true);
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await api.get("/auth/me");
        if (res.data?.user) {
          dispatch(authAction.setLoginUser(res.data.user));
        }
      } catch (err) {
        console.error("Kullanıcı bilgisi alınamadı:", err);
      }
    };
    fetchUser();
  }, [dispatch]);

  const usedAnnual = leaves
    .filter((l) => l?.owner?.tc === auth?.tc && l?.leavesType === LeaveTypeEnum.ANNUAL && l?.state === "APPROVED")
    .reduce((acc, l) => acc + (l?.totalDate || 0), 0);

  const usedSick = leaves
    .filter((l) => l?.owner?.tc === auth?.tc && l?.leavesType === LeaveTypeEnum.SICK && l?.state === "APPROVED")
    .reduce((acc, l) => acc + (l?.totalDate || 0), 0);

  const usedUnpaid = leaves
    .filter((l) => l?.owner?.tc === auth?.tc && l?.leavesType === LeaveTypeEnum.UNPAID && l?.state === "APPROVED")
    .reduce((acc, l) => acc + (l?.totalDate || 0), 0);

  const usedBirth = leaves
    .filter((l) => l?.owner?.tc === auth?.tc && l?.leavesType === LeaveTypeEnum.MATERNITY && l?.state === "APPROVED")
    .reduce((acc, l) => acc + (l?.totalDate || 0), 0);

  const totalAnnual = (auth?.remainingLeaveDays || 0) + (auth?.usedLeaveDays || 0);
  const remaining = auth?.remainingLeaveDays ?? 0;
  const isOver = remaining < 0;

  return (
    <div>
      {auth && (
        <div className="bg-white p-6 rounded-xl shadow-lg mb-8">
          <div className="flex items-start justify-between border-b pb-2 mb-4">
            <h2 className="text-2xl font-bold text-gray-800">{t("profile.title")}</h2>
            <button type="button" onClick={handleTransactions}>
              <img className="text-black fill-black" alt="" src={history} />
            </button>
          </div>

          <div className="flex gap-8">
            {/* Sol: Profil Bilgileri */}
            <div className="flex-1 space-y-3">
              <div className="flex gap-8 justify-center">
                <div>
                  <p className="text-sm text-gray-500 text-center">{t("profile.name")}</p>
                  <p className="text-base font-medium text-gray-700 text-center">{auth.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 text-center">{t("profile.surname")}</p>
                  <p className="text-base font-medium text-gray-700 text-center">{auth.surname}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 text-center">{t("profile.position")}</p>
                  <p className="text-base font-medium text-gray-700 text-center">{auth.position}</p>
                </div>
              </div>
              <div className="flex gap-8 justify-center">
                <div>
                  <p className="text-sm text-gray-500 text-center">{t("profile.startDate")}</p>
                  <p className="text-base font-medium text-gray-700 text-center">
                    {formatDate(auth.startDate)}
                  </p>
                </div>
              </div>
            </div>

            {/* Sağ: İzin Özeti */}
            <div className="border-l pl-8 flex flex-col gap-3 min-w-[320px]">
              <p className="text-sm font-semibold text-gray-600">{t("profile.summary")}</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-blue-50 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-500">{t("profile.totalAnnual")}</p>
                  <p className="text-lg font-bold text-blue-600">{totalAnnual} {t("profile.days")}</p>
                </div>
                <div className={`${isOver ? "bg-red-50" : "bg-green-50"} rounded-lg px-3 py-2`}>
                  <p className="text-xs text-gray-500">{t("profile.remainingAnnual")}</p>
                  <p className={`text-lg font-bold ${isOver ? "text-red-600" : "text-green-600"}`}>{remaining} {t("profile.days")}</p>
                  {isOver && <p className="text-xs text-red-500">{t("profile.overLimit")}</p>}
                </div>
                <div className="bg-yellow-50 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-500">{t("profile.usedAnnual")}</p>
                  <p className="text-lg font-bold text-yellow-600">{usedAnnual} {t("profile.days")}</p>
                </div>
                <div className="bg-red-50 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-500">{t("profile.sickLeave")}</p>
                  <p className="text-lg font-bold text-red-400">{usedSick} {t("profile.days")}</p>
                  <p className="text-xs text-gray-400">{t("profile.sickNote")}</p>
                </div>
                <div className="bg-gray-50 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-500">{t("profile.unpaidLeave")}</p>
                  <p className="text-lg font-bold text-gray-600">{usedUnpaid} {t("profile.days")}</p>
                </div>
                <div className="bg-pink-50 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-500">{t("profile.maternityLeave")}</p>
                  <p className="text-lg font-bold text-pink-500">{usedBirth} {t("profile.days")}</p>
                  <p className="text-xs text-gray-400">{t("profile.specialNote")}</p>
                </div>
              </div>
            </div>
          </div>

          <TransactionsModal
            isOpen={isModalOpen}
            onClose={() => setModalOpen(false)}
            transactions={transactions}
          />
        </div>
      )}
    </div>
  );
}

export default ProfileCard;